import { useCallback } from "react";
import {
  QueryBuilderConfig,
  QueryBuilderValue,
} from "../../data-model/queryBuilder";
import {
  addChildToGroup,
  createEmptyCondition,
  createEmptyGroup,
  getFieldDef,
  getOperatorDef,
  removeNodeById,
  updateNodeById,
} from "./queryBuilderUtils";

export const useQueryBuilder = (
  value: QueryBuilderValue,
  onChange: (value: QueryBuilderValue) => void,
  config: QueryBuilderConfig,
) => {
  const updateConditionField = useCallback(
    (conditionId: string, fieldValue: string) => {
      const fieldDef = getFieldDef(config, fieldValue);
      const firstOperator = fieldDef?.operators[0];
      onChange(
        updateNodeById(value, conditionId, (node) => ({
          ...node,
          field: fieldValue,
          operator: firstOperator?.value ?? "",
          value: firstOperator?.valueInputType === "multi-select" ? [] : "",
        })),
      );
    },
    [value, onChange, config],
  );

  const updateConditionOperator = useCallback(
    (conditionId: string, operatorValue: string, fieldValue: string) => {
      const fieldDef = getFieldDef(config, fieldValue);
      const operatorDef = fieldDef
        ? getOperatorDef(fieldDef, operatorValue)
        : undefined;
      const clearValue =
        operatorDef?.valueInputType === "multi-select" ? [] : "";
      onChange(
        updateNodeById(value, conditionId, (node) => ({
          ...node,
          operator: operatorValue,
          value: clearValue,
        })),
      );
    },
    [value, onChange, config],
  );

  const updateConditionValue = useCallback(
    (conditionId: string, newValue: string | string[]) => {
      onChange(
        updateNodeById(value, conditionId, (node) => ({
          ...node,
          value: newValue,
        })),
      );
    },
    [value, onChange],
  );

  const toggleGroupConjunction = useCallback(
    (groupId: string, conjunction: "AND" | "OR") => {
      onChange(
        updateNodeById(value, groupId, (node) => ({
          ...node,
          conjunction,
        })),
      );
    },
    [value, onChange],
  );

  const addCondition = useCallback(
    (groupId: string) => {
      onChange(addChildToGroup(value, groupId, createEmptyCondition()));
    },
    [value, onChange],
  );

  const addNestedGroup = useCallback(
    (groupId: string) => {
      onChange(addChildToGroup(value, groupId, createEmptyGroup()));
    },
    [value, onChange],
  );

  const removeNode = useCallback(
    (nodeId: string) => {
      onChange(removeNodeById(value, nodeId));
    },
    [value, onChange],
  );

  return {
    updateConditionField,
    updateConditionOperator,
    updateConditionValue,
    toggleGroupConjunction,
    addCondition,
    addNestedGroup,
    removeNode,
  } as const;
};
